import { useTranslation } from "react-i18next";

import type { SymbolOverride } from "../data";
import type { OverrideParty } from "./helpers";

import { InfoIcon } from "../../../Icons";
import { Typo } from "../../../Typo";
import { MASTER_COLOR_CLASS, PARTY_COLOR_CLASSES } from "./helpers";

/**
 * Dot + name + percentage per party.
 *
 * Order differs by surface, which is why it's a prop rather than fixed: rule
 * cards read Sub IB(s) first (matching the design's rule cards), the program
 * drawer's override detail reads You first.
 */
export const SplitLegend = ({
  rule,
  parties,
  youFirst = false,
}: {
  rule: SymbolOverride;
  parties: OverrideParty[];
  youFirst?: boolean;
}) => {
  const { t } = useTranslation();
  const masterItem = (
    <span key="master" className="inline-flex items-center gap-1.5">
      <span
        className={`h-2.5 w-2.5 shrink-0 rounded-sm ${MASTER_COLOR_CLASS}`}
      />
      <Typo element="span" type="bodySM" textColor="text-text-primary">
        {t("subIBReport_you", "You")}
      </Typo>
      <Typo element="span" type="bodySM" textColor="text-text-secondary">
        {rule.masterSplitPct}%
      </Typo>
    </span>
  );

  const partnerItems = rule.partnerSplits.map((split, index) => {
    const party = parties.find((p) => p.subIbId === split.subIbId);
    return (
      <span
        key={split.subIbId || index}
        className="inline-flex items-center gap-1.5"
      >
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-sm ${
            party?.colorClass ??
            PARTY_COLOR_CLASSES[index] ??
            PARTY_COLOR_CLASSES[0]
          }`}
        />
        <Typo element="span" type="bodySM" textColor="text-text-primary">
          {party?.label ?? t("subIBReport_defaultSubIb", "Sub IB")}
        </Typo>
        <Typo element="span" type="bodySM" textColor="text-text-secondary">
          {split.splitPct}%
        </Typo>
      </span>
    );
  });

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      {youFirst ? [masterItem, ...partnerItems] : [...partnerItems, masterItem]}
    </div>
  );
};

/**
 * Note shown when a symbol rule stacks on an asset-class rule. Two wordings:
 * `lead` is the editor's, naming the symbol being configured; the default is the
 * rule card's, where the symbol is already the card's heading. Kept as whole
 * sentences so only the scope names are variable.
 */
export const ShadowNote = ({
  assetClass,
  symbol,
  lead = false,
}: {
  assetClass: string;
  symbol?: string;
  lead?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-2">
      <span className="shrink-0">
        <InfoIcon
          className="h-4 w-4 [&_path]:stroke-icon-brand-default"
          aria-hidden="true"
          focusable="false"
        />
      </span>
      <Typo element="span" type="bodyXS" textColor="text-text-secondary">
        {lead
          ? t(
              "subIBReport_overrideShadowLead",
              "This overrides the {{assetClass}} rule for {{symbol}}",
              { assetClass, symbol },
            )
          : t(
              "subIBReport_overrideShadowNote",
              "Overrides the {{assetClass}} rule",
              { assetClass },
            )}
      </Typo>
    </span>
  );
};
